export type Option = { value: string; label: string };

export const FOLLOWER_RANGES: Option[] = [
  { value: "under_1k", label: "<1K" },
  { value: "k1_5", label: "1K–5K" },
  { value: "k5_10", label: "5K–10K" },
  { value: "k10_25", label: "10K–25K" },
  { value: "k25_50", label: "25K–50K" },
  { value: "k50_100", label: "50K–100K" },
  { value: "k100_plus", label: "100K+" },
];

export const AGE_RANGES: Option[] = [
  { value: "age_18_less", label: "<18" },
  { value: "age_18_24", label: "18–24" },
  { value: "age_25_34", label: "25–34" },
  { value: "age_35_44", label: "35–44" },
  { value: "age_45_54", label: "45–54" },
  { value: "age_55_plus", label: "55>" },
];

export const GENDERS: Option[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  // { value: "prefer_not_to_say", label: "Prefer not to say" },
];

export const OCCUPATIONS: Option[] = [
  { value: "student", label: "Student" },
  { value: "working_adult", label: "Working Adult" },
  { value: "parent", label: "Parent" },
  { value: "homemaker", label: "Homemaker" },
  { value: "self_employed", label: "Self-employed" },
  { value: "others", label: "Others" },
];

export const LANGUAGES: Option[] = [
  { value: "bahasa malaysia", label: "Bahasa Malaysia" },
  { value: "english", label: "English" },
  { value: "mandarin", label: "Mandarin" },
  { value: "cantonese", label: "Cantonese" },
  { value: "tamil", label: "Tamil" },
  { value: "others", label: "Others" },
];

export const INTERESTS: Option[] = [
  { value: "food", label: "Food" },
  { value: "beauty", label: "Beauty" },
  { value: "parenting", label: "Parenting" },
  { value: "family", label: "Family" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "travel", label: "Travel" },
  { value: "home", label: "Home" },
  { value: "tech", label: "Tech" },
  { value: "fitness", label: "Fitness" },
  { value: "fashion", label: "Fashion" },
  { value: "gaming", label: "Gaming" },
  { value: "automotive", label: "Automotive" },
  { value: "pets", label: "Pets" },
  { value: "finance", label: "Finance" },
  { value: "others", label: "Others" },
];

export const STYLES: Option[] = [
  { value: "reviews", label: "Reviews" },
  { value: "vlogs", label: "Vlogs" },
  { value: "tutorials", label: "Tutorials" },
  { value: "unboxing", label: "Unboxing" },
  { value: "storytelling", label: "Storytelling" },
  { value: "talking_to_camera", label: "Talking-to-camera" },
  { value: "voiceover", label: "Voiceover" },
  { value: "aesthetic_content", label: "Aesthetic Content" },
];

export const CAMPAIGN_TYPES: Option[] = [
  { value: "product_sampling", label: "Product Sampling" },
  { value: "events", label: "Events / Appearance" },
  { value: "ugc", label: "UGC-only (no posting)" },
  { value: "affiliate_campaigns", label: "Affiliate Campaigns" },
];

export const COMPENSATION: Option[] = [
  { value: "paid", label: "Paid" },
  { value: "product_only", label: "Product-only" },
  { value: "both", label: "Both" },
];

export const AREAS: Option[] = [
  { value: "klang_valley", label: "Klang Valley" },
  { value: "penang", label: "Penang" },
  { value: "johor", label: "Johor" },
  { value: "east_malaysia", label: "East Malaysia" },
  { value: "central", label: "Central" },
  { value: "north", label: "North" },
  { value: "north_east", label: "North-East" },
  { value: "east", label: "East" },
  { value: "west", label: "West" },
  { value: "others", label: "Others" },
];

export const COUNTRIES: Option[] = [
  { value: "Malaysia", label: "Malaysia" },
  { value: "Singapore", label: "Singapore" },
];

// Alphabetical — shown as a sub-dropdown once "Malaysia" is picked above.
export const MY_STATES: Option[] = [
  "Johor",
  "Kedah",
  "Kelantan",
  "Kuala Lumpur",
  "Labuan",
  "Melaka",
  "Negeri Sembilan",
  "Pahang",
  "Penang",
  "Perak",
  "Perlis",
  "Putrajaya",
  "Sabah",
  "Sarawak",
  "Selangor",
  "Terengganu",
].map((name) => ({ value: name, label: name }));

export const HOW_DID_YOU_HEAR: Option[] = [
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "friend_or_creator", label: "Friend or Creator" },
  { value: "kd_website", label: "KD Website" },
  { value: "kd_campaign", label: "KD Campaign" },
  { value: "search_engine", label: "Search engine" },
  { value: "others", label: "Others" },
];

export type PlatformOption = {
  value: string;
  name: string;
  postTypes: Option[];
};

export const PLATFORMS: PlatformOption[] = [
  {
    value: "tiktok",
    name: "TikTok",
    postTypes: [
      { value: "video", label: "Video" },
      { value: "live", label: "LIVE" },
      { value: "photo_post", label: "Photo post" },
    ],
  },
  {
    value: "instagram",
    name: "Instagram",
    postTypes: [
      { value: "reel", label: "Reel" },
      { value: "feed_post", label: "Feed post" },
      { value: "story", label: "Story" },
      { value: "carousel", label: "Carousel" },
    ],
  },
  {
    value: "xiaohongshu",
    name: "Xiaohongshu",
    postTypes: [
      { value: "image_note", label: "Image note" },
      { value: "video_note", label: "Video note" },
    ],
  },
  {
    value: "lemon8",
    name: "Lemon8",
    postTypes: [{ value: "post", label: "Post" }],
  },
  {
    value: "youtube",
    name: "YouTube",
    postTypes: [
      { value: "long_form_video", label: "Long-form video" },
      { value: "shorts", label: "Shorts" },
      { value: "integration_mention", label: "Integration / mention" },
    ],
  },
  {
    value: "facebook",
    name: "Facebook",
    postTypes: [
      { value: "reel", label: "Reel" },
      { value: "feed_post", label: "Feed post" },
      { value: "story", label: "Story" },
      { value: "video", label: "Video" },
    ],
  },
  {
    value: "x",
    name: "X",
    postTypes: [
      { value: "post", label: "Post" },
      { value: "thread", label: "Thread" },
      { value: "video_post", label: "Video post" },
    ],
  },
  {
    value: "threads",
    name: "Threads",
    postTypes: [
      { value: "post", label: "Post" },
      { value: "photo_post", label: "Photo post" },
      { value: "video_post", label: "Video post" },
    ],
  },
];

export const POST_TYPES_BY_PLATFORM: Record<string, Option[]> = PLATFORMS.reduce(
  (acc, p) => ({ ...acc, [p.value]: p.postTypes }),
  {} as Record<string, Option[]>
);
